import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Container, Input, Form, Card, CardBody, Modal, ModalHeader, ModalBody } from "reactstrap";
import debounce from 'lodash.debounce';
//Import Breadcrumb
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import withRouter from "src/components/Common/withRouter";
import Navbar from "src/components/HorizontalLayout/Navbar";
import {
  getUsers as onGetUsers,
  addNewUser,
  deleteUser,
  updateUser,
  loginById,
} from "../../../slices/thunks";

//redux
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";

import DeleteModal from "src/common/DeleteModal";
import { createSelector } from "reselect";
import DataTable from "react-data-table-component";
import { setAgency } from "src/slices/Auth/Agency/thunk";
import { RootState } from "../../../slices/store";
import axiosInstance from "src/common/axiosInstance";
import TableCustomLoader from '../../../components/Common/TableCustomLoader';
import SelectAgencyModal from "src/pages/Authentication/SelectAgencyModal";
import { toast } from 'react-toastify';
import {
  setSelectedAgency,
  setSelectedStatus,
  setSelectedOrder,
  setSelectedLineItems,
  setStartDate,
  setEndDate,
} from "../../../slices/Filters/reducer";
import SendInviteModal from "./SendInviteModal";
import { sendInvite } from "src/components/Common/sentInvite";
import { usePageAccess, PAGE_ACCESS_TYPES } from '../../../components/Common/useAuthRedirect';
import useIsMobile from "src/hooks/useIsMobile";
import "./clientUsers.css"
import Pagination from "src/components/Common/Pagination";

interface User {
  users_id: number;
  name: string;
  permission_type: string;
  email: string;
  tags: string[];
  projects: number;
  status: string;
  profile_picture_url: null;
  last_login: string;
  var_key: string | null;
}

interface UsersResponse {
  data: User[];
  limit: number;
  page: number;
  total: number;
}

const UserList = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agencies, setAgencies] = useState<any[]>([]);
  const [modalLarge, setModalLarge] = useState(false);
  const [modalLargeInvite, setModalLargeInvite] = useState(false);
  const [userId, setUserId] = useState(Number);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [agencyLoading, setAgencyLoading] = useState(false);
  const userState = useSelector((state: RootState) => state.login);
  const selectLeadData = createSelector(
    (state: any) => state.contacts.users,
    (leadsChart) => leadsChart
  );
  function togLarge() {
    setModalLarge(!modalLarge);
    removeBodyCss();
  }

  function togLargeInvite() {
    setModalLargeInvite(!modalLargeInvite);
    removeBodyCss();
  }
  function removeBodyCss() {
    document.body.classList.add("no_padding");
  }

  async function setAgencyState(id: string | number, users_id?: string | number) {
    setAgencyLoading(true);

    // Close modal if open
    if (modalLarge) {
      setModalLarge(false);
    }

    try {
      if (userState.user.user.user_type != 'Client')
        dispatch(setSelectedAgency(''));
      dispatch(setSelectedOrder(''));
      dispatch(setSelectedLineItems(''));
      dispatch(setStartDate(''));
      dispatch(setEndDate(''));

      // Wait for login to complete first
      await dispatch(loginById(users_id));

      // Then wait for agency setup to complete before navigating
      if (id) {
        await dispatch(setAgency({ agency_id: id }, navigate, true));
      } else {
        await dispatch(setAgency({ agency_id: null }, navigate, true));
      }

      // Add 500ms delay to ensure Redux state propagates
      await new Promise(resolve => setTimeout(resolve, 500));

    } catch (error) {
      console.error('Login failed:', error);
      toast.error('Failed to login as user. Please try again.');
      setAgencyLoading(false);
    }
  }

  const handleUserLogin = async (user: User) => {
    if (user.status == "Inactive") {
      toast.error("Account inactive. Unable to log in.");
      return;
    }

    try {
      const currentAuthUser = localStorage.getItem("authUser");
      if (currentAuthUser) {
        localStorage.setItem("adminUser", currentAuthUser);
      }

      // Fetch agencies
      const response = await axiosInstance.get(`/agencies`, {
        params: {
          email: user.email, // Passing email as a query parameter
        },
      });

      const res = await response.data;
      if (res && res.data) {
        setAgencies(res.data);
        if (res.data.length > 1) {
          setUserId(user.users_id);
          togLarge(); // Show agency selection modal if multiple agencies are found
        } else {
          const result = res.data;
          if (Object.keys(result).length) {
            const id = result[0].account_id;
            if (id) {
              setAgencyState(id, user.users_id);
            }
          } else {
            setAgencyState('', user.users_id);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch agencies:', error);
      toast.error('Failed to fetch user agencies. Please try again.');
    }
  };

  const agencyData = useSelector((state: any) => state.agency.agency.data);
  const users = useSelector(selectLeadData) as UsersResponse;
  const [userList, setUserList] = useState<User | null>(null);
  const [modal, setModal] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [inviteUser, setInviteUser] = useState([]);
  const [sortColumn, setSortColumn] = useState<string>("last_login");
  const [sortDirection, setSortDirection] = useState<string>("desc");

  React.useEffect(() => {
    setLoading(true);
  }, []);

  const authUser = useMemo(() => {
    const user = localStorage.getItem("authUser");
    return user ? JSON.parse(user) : null;
  }, []);

  // Debounce search term updates
  const debouncedSearch = useMemo(
    () => debounce((value: string) => {
      setDebouncedSearchTerm(value);
      setPage(1);
    }, 500),
    []
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const sendInvitation = async (e: any) => {
    if (e.uds_metrics == null) {
      toast.error("Please save the user's metrics before sending invitation.");
      return;
    }
    try {
      const response = await axiosInstance.get(`/agencies`, {
        params: {
          email: e.email, // Passing email as a query parameter
        },
      });

      const res = await response.data;
      const updatedAgencies = [...res.data];
      if (updatedAgencies) {
        setAgencies(updatedAgencies);
        setInviteUser(e);
        togLargeInvite();
      }
    } catch (error) {
      console.log(error)
      toast.error("Something Went Wrong!");
    }
  };

  const sendInviteMail = async (id: string | number, inviteduser: any, users_id?: string | number) => {
    try {
      let response;
      response = await axiosInstance.get(`/agency-info`, {
        params: {
          agency_id: id, // Passing agency id as a query parameter
        },
      });
      const agencyData = await response.data;
      sendInvite(inviteduser, agencyData);
      dispatch(onGetUsers({
        agency_id: agencyData.agency_id,
        page,
        limit,
        search: debouncedSearchTerm,
        sort_column: sortColumn,
        sort_direction: sortDirection
      }));
      setTimeout(() => {
        togLargeInvite();
      }, 2000);
    } catch (error) {
      console.log(error)
    }
  }
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: userList?.name || "",
      designation: userList?.permission_type || "",
      tags: userList?.tags || [],
      email: userList?.email || "",
      projects: userList?.projects || 0,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please Enter Your Name"),
      designation: Yup.string().required("Please Enter Your Designation"),
      tags: Yup.array().of(Yup.string()).required("Please Enter Tag"),
      email: Yup.string().email().required("Please Enter Your Email"),
      projects: Yup.number().required("Please Enter Your Project"),
    }),
    onSubmit: (values) => {
      if (isEdit) {
        const updatedUser: User = {
          ...userList!,
          name: values.name,
          permission_type: values.designation,
          tags: values.tags,
          email: values.email,
          projects: values.projects,
        };
        dispatch(updateUser(updatedUser));
        validation.resetForm();
        setIsEdit(false);
      } else {
        const newUser: User = {
          users_id: Math.floor(Math.random() * (30 - 20)) + 20,
          name: values.name,
          permission_type: values.designation,
          email: values.email,
          tags: values.tags,
          projects: values.projects,
          status: "Active",
          profile_picture_url: null,
          last_login: '',
          var_key: null,
        };
        // save new user
        dispatch(addNewUser(newUser));
        validation.resetForm();
      }
      toggle();
    },
  });

  usePageAccess(PAGE_ACCESS_TYPES.RESTRICTED_USERS);

  const isMobile = useIsMobile();

  useEffect(() => {
    dispatch(
      onGetUsers({
        agency_id: agencyData.agency_id,
        page,
        limit,
        search: debouncedSearchTerm,
        sort_column: sortColumn,
        sort_direction: sortDirection
      })
    ).then((response: any) => {
      setLoading(false);
      if (response && response.payload) {
        const resData = response.payload as UsersResponse;
        setTotal(resData.total);
      }
    });
  }, [agencyData?.agency_id, page, limit, debouncedSearchTerm, sortColumn, sortDirection]);

  useEffect(() => {
    setIsEdit(false);
  }, [users]);

  const toggle = () => {
    setModal((prev) => !prev);
    if (!modal) setUserList(null);
  };

  const handleUserClick = (user: User) => {
    setUserList(user);
    setIsEdit(true);
    toggle();
  };

  const handleDeleteUser = () => {
    if (userList) {
      setLoading(true);
      dispatch(deleteUser(userList.users_id)).then(() => {
        // Re-fetch users after successful deletion
        dispatch(
          onGetUsers({
            agency_id: agencyData.agency_id,
            page: 1,
            limit,
            search: searchTerm,
            sort_column: sortColumn,
            sort_direction: sortDirection
          })
        ).then((response: any) => {
          setLoading(false);
          if (response && response.payload) {
            const resData = response.payload as UsersResponse;
            setTotal(resData.total);
          }
        });
        setPage(1);
        setDebouncedSearchTerm(searchTerm);
      });
      setDeleteModal(false);
      toast.success("User deleted successfully");
    }
  };

  const userData = users?.data || [];
  const displayUsers = userData;

  const totalPages = Math.ceil(total / limit);

  const getInitials = (name: string) => {
    if (!name) return '';
    const words = name.trim().split(' ').filter(Boolean);
    const firstInitial = words[0]?.charAt(0).toUpperCase() || '';
    const secondInitial = words.length > 1 ? words[1]?.charAt(0).toUpperCase() : '';
    return firstInitial + secondInitial;
  };

  const handleSort = (column: string) => {
    if (column === sortColumn) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column with default direction
      setSortColumn(column);
      setSortDirection(column === 'last_login' ? 'desc' : 'asc');
    }
    setPage(1); // Reset to first page when sorting
  };

  const columns = [
    {
      name: (
        <div 
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={() => handleSort('name')}
        >
          Name
          {sortColumn === 'name' && (
            <i className={`mdi mdi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
          )}
        </div>
      ),
      cell: (row: User) => (
        <div className="d-flex align-items-center">
          <div className="flex-shrink-0">
            {row.profile_picture_url ? (
              <img
                src={row.profile_picture_url}
                alt=""
                className="rounded-circle avatar-sm"
              />
            ) : (
              <div
                className="rounded-circle avatar-sm d-flex align-items-center justify-content-center text-white"
                style={{
                  fontWeight: 'bold',
                  fontSize: "12px",
                  backgroundColor: "#607d8b"
                }}
              >
                {getInitials(row.name)}
              </div>
            )}
          </div>
          <div className="flex-grow-1 ms-3" style={{ marginTop: "16px" }}>
            <p style={{ fontWeight: "500" }}>{row.name}</p>
          </div>
        </div>
      ),
      selector: (row: User) => row.name,
      sortable: false,
      width: '250px',
    },
    {
      name: (
        <div 
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={() => handleSort('permission_type')}
        >
          Client Type
          {sortColumn === 'permission_type' && (
            <i className={`mdi mdi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
          )}
        </div>
      ),
      selector: (row: User) => row.permission_type?.toLowerCase() === 'admin' ? 'Admin' : 'Standard',
      sortable: false,
      width: '120px',
    },
    {
      name: (
        <div 
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={() => handleSort('email')}
        >
          Email
          {sortColumn === 'email' && (
            <i className={`mdi mdi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
          )}
        </div>
      ),
      selector: (row: User) => row.email,
      sortable: false,
    },
    {
      name: (
        <div 
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={() => handleSort('last_login')}
        >
          Last Login
          {sortColumn === 'last_login' && (
            <i className={`mdi mdi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
          )}
        </div>
      ),
      selector: (row: User) => {
        if (!row.last_login) return "";

        const date = new Date(row.last_login);
        const day = date.toLocaleDateString("en-GB", { day: "2-digit" });
        const month = date.toLocaleDateString("en-GB", { month: "short" });
        const year = date.getFullYear();

        const time = date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
        .replace("am", "AM")
        .replace("pm", "PM");
        return `${day}-${month}-${year} ${time}`;
      },
      sortable: false,
      width: "200px",
    },
    {
      name: (
        <div 
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={() => handleSort('status')}
        >
          Status
          {sortColumn === 'status' && (
            <i className={`mdi mdi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
          )}
        </div>
      ),
      cell: (row: User) => (
        <div className="d-flex align-items-center">
          <div className="flex-shrink-0">
            {row.status.toLowerCase() == 'active' ? (
              <i className="mdi mdi-check-circle-outline font-size-18 text-success"></i>
            ) : (
              <i className="mdi mdi-close-circle-outline font-size-18 text-danger"></i>
            )}
          </div>
          <div className="flex-grow-1 ms-2" style={{ marginTop: "16%" }}>
            <p>
              {row.status
                ? row.status.charAt(0).toUpperCase() + row.status.slice(1).toLowerCase()
                : ""}
            </p>
          </div>
        </div>
      ),
      selector: (row: User) => row.status.charAt(0).toUpperCase() + row.status.slice(1),
      sortable: false,
      width: '150px',
    },
    {
      name: "Action",
      cell: (row: User) => (
        <div className="d-flex">
          <button
            style={{ border: "none", backgroundColor: "transparent", }}
            onClick={() => {
              navigate("/add-edit-user", { state: { user: row } });
            }}
            title="Edit"
          >
            <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
          </button>
          <button
            style={{ border: "none", backgroundColor: "transparent", }}
            onClick={() => {
              setUserList(row);
              setDeleteModal(true);
            }}
            title="Delete"
          >
            <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
          </button>
          <button
            style={{ border: "none", backgroundColor: "transparent", }}
            onClick={() => handleUserLogin(row)}
            title="Sign-In"
          >
            <i className="uil uil-sign-out-alt font-size-16 text-success"></i>
          </button>
          {row.status === "Inactive" && (
            <button
              style={{ border: "none", backgroundColor: "transparent", }}
              onClick={() => {
                sendInvitation(row);
              }}
              title={row.var_key === '' || row.var_key === null ? "Invite" : "Re-Invite"}
            >
              {row.status === 'Inactive' && (row.var_key === '' || row.var_key === null) ? (
                <i className="uil uil-envelope-alt font-size-18" style={{ color: "aqua" }}></i>
              ) : (
                <i className="uil uil-envelope-redo font-size-18 text-danger"></i>
              )}
            </button>
          )}
        </div>
      ),
      width: '160px',
    },
  ];



  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteUser}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content" style={{ marginTop: "0" }}>
        <Navbar />
        <Container fluid style={{ maxWidth: "100%", marginTop: 10 }}>
          <Breadcrumbs title="Settings" breadcrumbItem="Client Users" titleLink="/branding" />
          <>
            <Row>
              <Col lg="12">
                <Card>
                  <CardBody>
                    {/* Search Bar */}
                    <div className="search-header">
                      <Input
                        type="text"
                        placeholder="Search by name or email"
                        value={searchTerm}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSearchTerm(value);
                          debouncedSearch(value);
                        }}
                        className="search-input"
                        style={{ fontSize: "16px" }}
                      />
                      <button type="button"
                        className="btn btn-primary button-primary"
                        onClick={() => { navigate("/add-edit-user"); }}>
                        <i className="uil uil-plus me-1"></i>
                        Add New User
                      </button>
                    </div>
                    <div className="table-responsive">
                    {loading ? (
                      <TableCustomLoader length={2}
                        message="Almost ready! Getting users data" />
                    ) : (
                      <>
                      {
                        !isMobile ? (
                        <DataTable
                          className="data-table"
                          columns={columns}
                          data={displayUsers}
                          highlightOnHover
                        />
                        )
                        :
                        <div className="d-block d-md-none mobile-cards-container">
                          {displayUsers.map((user: any) => {
                            return (
                              <div className="mobile-card card " key={user.userId}>
                                <div className="d-flex align-items-center">
                                  <div className="flex-shrink-0">
                                    {user.profile_picture_url ? (
                                      <img
                                        src={user.profile_picture_url}
                                        alt=""
                                        className="rounded-circle avatar-sm"
                                      />
                                    ) : (
                                      <div
                                        className="rounded-circle avatar-sm d-flex align-items-center justify-content-center text-white"
                                        style={{
                                          fontWeight: 'bold',
                                          fontSize: '12px',
                                          backgroundColor: "#607d8b"
                                        }}
                                      >
                                        {getInitials(user.name)}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-grow-1 ms-3" style={{ marginTop: "16px" }}>
                                    <p style={{ fontWeight: "500" }}>{user.name}</p>
                                  </div>
                                </div>
                                <div
                                  className="mobile-card-row"
                                >
                                  <span className="row-title fw-bold">
                                    Client Type
                                  </span>
                                  <span className="row-value">
                                    {user.permission_type?.toLowerCase() === 'admin' ? 'Admin' : 'Standard'}
                                  </span>
                                </div>
                                <div
                                  className="mobile-card-row "
                                >
                                  <span className="row-title fw-bold">
                                    Email
                                  </span>
                                  <span className="row-value">
                                    {user.email}
                                  </span>
                                </div>
                                <div
                                  className="mobile-card-row "

                                >
                                  <span className="row-title fw-bold">
                                    last Login
                                  </span>
                                  <span className="row-value">
                                    {user.last_login}
                                  </span>

                                </div>
                                <div
                                  className="mobile-card-row "

                                >
                                  <span className="row-title fw-bold">
                                    Status
                                  </span>
                                  <span className="row-value">
                                    {user.status}
                                  </span>

                                </div>
                                <div className="mobile-action-row">
                                  <button
                                    onClick={() => {
                                      navigate("/add-edit-user", { state: { user: user } });
                                    }}
                                    title="Edit"
                                    className="edit-btn"
                                  >
                                    Edit
                                    <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                                  </button>
                                  <button
                                    className="delete-btn"
                                    onClick={() => {
                                      setUserList(user);
                                      setDeleteModal(true);
                                    }}
                                    title="Delete"
                                  >
                                    Delete
                                    <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                                  </button>
                                  <button
                                    onClick={() => handleUserLogin(user)}
                                    title="Sign-In"
                                    className="signin-btn"
                                  >
                                    Sign In
                                    <i className="uil uil-sign-out-alt font-size-16 text-success"></i>
                                  </button>
                                  {user.status === "Inactive" && (
                                    <button
                                      onClick={() => {
                                        sendInvitation(user);
                                      }}
                                      title={user.var_key === '' || user.var_key === null ? "Invite" : "Resend Invite"}
                                      className={` ${user.status === 'Inactive' && (user.var_key === '' || user.var_key === null)
                                          ? 'invite-inactive-btn'
                                          : 'invite-active-btn'
                                        }`}
                                    >
                                      {user.status === 'Inactive' && (user.var_key === '' || user.var_key === null) ? (
                                        <>
                                          <i className="uil uil-envelope-alt font-size-18" style={{ color: "aqua" }}></i>
                                          Invite
                                        </>
                                      ) : (
                                        <>
                                          <i className="uil uil-envelope-redo font-size-18 text-danger"></i>
                                        </>
                                      )}
                                    </button>
                                  )}
                                </div>

                              </div>
                            );
                          })}
                        </div>
                     }
                      </>
                    )}
                    </div>
                    {displayUsers.length > 0 &&
                    <Pagination
                          totalPages={totalPages}
                          currentPage={page}
                          onPageChange={(newPage) => setPage(newPage)}
                          currentCount={displayUsers.length}
                          pageSize={limit}
                          onPageSizeChange={(newLimit) => {
                            setLimit(newLimit);
                            setPage(1);
                          }}
                        />
                    }
                    <Modal isOpen={modal} toggle={toggle}>
                      <ModalHeader toggle={toggle} tag="h4">
                        {isEdit ? "Edit User" : "Add User"}
                      </ModalHeader>
                      <ModalBody>
                        <Form
                          onSubmit={(e) => {
                            e.preventDefault();
                            validation.handleSubmit();
                          }}
                        >
                        </Form>
                      </ModalBody>
                    </Modal>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
          <Row className="my-4">
            <SelectAgencyModal
              isOpen={modalLarge}
              toggle={togLarge}
              agencies={agencies}
              onSelectAgency={setAgencyState}
              userId={userId}
            />
          </Row>

          <Row className="my-4">
            <SendInviteModal
              isOpen={modalLargeInvite}
              toggle={togLargeInvite}
              agencies={agencies}
              onSelectAgencyForInvite={sendInviteMail}
              userId={userId}
              inviteduser={inviteUser}
            />
          </Row>
        </Container>
      </div>

      {/* Loading Overlay */}
      {agencyLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div className="spinner-border text-light" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-light mt-3" style={{ fontSize: '16px', fontWeight: '500' }}>
            Logging in , please wait...
          </p>
        </div>
      )}
    </React.Fragment>
  );
};

export default withRouter(UserList);
